import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { RefreshCw, Search, SlidersHorizontal } from 'lucide-react';
import { RunApi, type CriterionDef, type ColumnLookupRow } from './api';
import type { ColumnLookup } from './types';
import LookupPickerDialog from './LookupPickerDialog';
import LookupConfigDialog from './LookupConfigDialog';
import { applyCriteriaSettings } from '@twasta/page-runtime/customize';

interface Props {
  sourceSql: string;
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Notifies the parent of the lower-cased names that the criteria form
   *  currently manages. The parent can hide these from any raw-parameter
   *  editor so the same value isn't shown in two places. */
  onCriteriaNames?: (names: string[]) => void;
  /** Notifies the parent of the friendly label for each (lower-cased)
   *  criterion name, so a collapsed recap can show "Location Code" instead
   *  of the raw `location_code` technical name. */
  onCriteriaLabels?: (labels: Record<string, string>) => void;
  /** Compact, responsive multi-column layout (narrower min column width).
   *  Used on the Review tab where space is tight. */
  dense?: boolean;
  /** Reference-table lookups attached to criteria, keyed by (any-case)
   *  criterion name. A criterion with a lookup shows a 🔍 picker. */
  lookups?: Record<string, ColumnLookup>;
  /** Caption per criterion, keyed by (any-case) criterion name — overrides the
   *  label humanised from the placeholder name (the model's `criteria_labels`). */
  labels?: Record<string, string>;
  /** When provided, the form is in DEFINE mode: each criterion gets a config
   *  button to add/edit/remove its lookup. Omit to only show/use lookups. */
  onChangeLookups?: (next: Record<string, ColumnLookup>) => void;
  /** Search a criterion's reference table for the picker. The parent binds the
   *  current criteria values for any {name} filter placeholders. */
  fetchLookup?: (criterion: string, search: string) => Promise<ColumnLookupRow[]>;
  /** The process's `criteria_settings` (label / order / hidden / read-only /
   *  default per criterion — authored, or customized by the tenant's
   *  functional administrator). Hidden criteria are not shown and read-only
   *  ones cannot be changed; the server enforces both. */
  criteriaSettings?: Record<string, unknown>;
}

// ── Styling mirrors the transaction designer's FormRenderer field exactly, so a
// gen-process criterion looks identical to a transaction form input. Values are
// the literals/CSS-variables used in packages/page-runtime/src/txn/FormRenderer.

const labelTextStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
  letterSpacing: 0.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Editable inputs get the white-in-light --lp-input-bg background (same as a
// transaction form field), NOT the greyed secondary background.
const fieldStyle: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 4,
  border: '1px solid var(--color-border)',
  background: 'var(--lp-input-bg)',
  color: 'var(--color-text)',
  fontSize: '0.9125rem',
  fontFamily: 'inherit',
  outline: 'none',
};

const lookupBtnStyle: CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  flex: '0 0 auto',
};

function toDateInput(v: unknown): string {
  if (!v) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  const s = String(v);
  // accept YYYY-MM-DD or ISO; pick the date part.
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

function toListItems(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x));
  if (v == null || v === '') return [];
  return String(v).split(',').map((s) => s.trim()).filter(Boolean);
}

function toRangePair(v: unknown): [string, string] {
  if (Array.isArray(v)) {
    return [v[0] != null ? String(v[0]) : '', v[1] != null ? String(v[1]) : ''];
  }
  if (v == null || v === '') return ['', ''];
  const parts = String(v).split(',').map((s) => s.trim());
  return [parts[0] ?? '', parts[1] ?? ''];
}

// A criterion with no seeded value (no current value, no default, no auto-default
// helper) must be supplied by the user — show the same red mandatory asterisk a
// transaction form uses.
function isMandatory(c: CriterionDef): boolean {
  const hasSeed = (c.value != null && c.value !== '')
    || (c.default != null && c.default !== '')
    || !!c.default_helper;
  return !hasSeed;
}

export default function CriteriaForm({
  sourceSql, values, onChange, onCriteriaNames, onCriteriaLabels, dense,
  lookups, onChangeLookups, fetchLookup, labels, criteriaSettings,
}: Props) {
  const [rawItems, setItems] = useState<CriterionDef[]>([]);
  const items = useMemo(
    () => applyCriteriaSettings(rawItems as any[], criteriaSettings as any) as CriterionDef[],
    [rawItems, criteriaSettings],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  // Open dialogs: picking a value, or (define mode) configuring the lookup.
  const [pickerFor, setPickerFor] = useState<CriterionDef | null>(null);
  const [configFor, setConfigFor] = useState<CriterionDef | null>(null);

  // An organization's customized label (criteria_settings, already applied to
  // c.label) wins; then the model's own caption; then the humanised name.
  const labelOf = (c: CriterionDef): string => {
    const lower = c.name.toLowerCase();
    const sk = criteriaSettings ? Object.keys(criteriaSettings).find((x) => x.toLowerCase() === lower) : undefined;
    const custom = sk ? String((criteriaSettings as any)?.[sk]?.label ?? '').trim() : '';
    if (custom) return custom;
    const k = labels ? Object.keys(labels).find((x) => x.toLowerCase() === lower) : undefined;
    return (k && labels?.[k]?.trim()) || c.label || c.name;
  };

  // Push the detected criterion names up to the parent whenever the parsed
  // list changes. Stored as lower-cased strings so callers can compare
  // case-insensitively against legacy raw-param keys.
  const namesRef = useRef<string>('');
  useEffect(() => {
    const names = items.map((i) => i.name.toLowerCase()).sort();
    const fingerprint = names.join('|');
    if (fingerprint !== namesRef.current) {
      namesRef.current = fingerprint;
      onCriteriaNames?.(names);
    }
  }, [items, onCriteriaNames]);

  // Lift the friendly label for each criterion up to the parent (keyed by the
  // lower-cased name) so a collapsed recap can show the human-readable label.
  const labelsRef = useRef<string>('');
  useEffect(() => {
    const labels: Record<string, string> = {};
    for (const i of items) labels[i.name.toLowerCase()] = labelOf(i);
    const fingerprint = JSON.stringify(labels);
    if (fingerprint !== labelsRef.current) {
      labelsRef.current = fingerprint;
      onCriteriaLabels?.(labels);
    }
  }, [items, onCriteriaLabels, labels]);

  // Re-parse the SQL whenever it changes (debounced); pull the latest
  // prompt list from the backend so operator/type inference stays in sync.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!sourceSql || !sourceSql.includes('?.')) {
      setItems([]);
      setError(null);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      RunApi.criteria({ source_sql: sourceSql })
        .then((r) => {
          setItems(r.items);
          // Seed any default values into the parent values map (without
          // overwriting user input — except a hidden / read-only criterion,
          // which always carries its default).
          const seed: Record<string, unknown> = { ...values };
          let mutated = false;
          for (const c of applyCriteriaSettings(r.items as any[], criteriaSettings as any) as any[]) {
            const key = c.name;
            const lower = key.toLowerCase();
            const fixed = (c.hidden || c.protect) && c.value !== undefined && c.value !== '';
            if (fixed) {
              for (const k of Object.keys(seed)) if (k.toLowerCase() === lower) delete seed[k];
            }
            const has = Object.keys(seed).some((k) => k.toLowerCase() === lower);
            if (!has) {
              if (c.value !== undefined) { seed[key] = c.value; mutated = true; }
              else if (c.default !== undefined) { seed[key] = c.default; mutated = true; }
            }
          }
          if (mutated) onChange(seed);
        })
        .catch((e) => setError((e as Error).message))
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceSql]);

  const valueFor = (name: string): unknown => {
    const lower = name.toLowerCase();
    const k = Object.keys(values).find((x) => x.toLowerCase() === lower);
    return k ? values[k] : undefined;
  };

  const setValue = (name: string, v: unknown) => {
    // Drop any case-variant existing keys; write the canonical name.
    const lower = name.toLowerCase();
    const next: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(values)) {
      if (k.toLowerCase() !== lower) next[k] = val;
    }
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
      // leave it out so backend can fall back to default
    } else {
      next[name] = v;
    }
    onChange(next);
  };

  const renderInput = (c: CriterionDef) => {
    const cur = valueFor(c.name);
    if (c.multi === 'list') {
      const arr = toListItems(cur);
      return (
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            type="text"
            placeholder="comma-separated, e.g. east, north, south"
            value={arr.join(', ')}
            onChange={(e) => setValue(c.name, toListItems(e.target.value))}
            style={fieldStyle}
          />
          {arr.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
              {arr.map((x, i) => (
                <span key={i} style={chipStyle}>{x}</span>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (c.multi === 'range') {
      const [a, b] = toRangePair(cur);
      const inputType = c.data_type === 'date' ? 'date' : (c.data_type === 'number' ? 'number' : 'text');
      return (
        <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
          <label style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ ...labelTextStyle, fontSize: '0.8125rem' }}>from</span>
            <input
              type={inputType}
              value={inputType === 'date' ? toDateInput(a) : a}
              onChange={(e) => setValue(c.name, [e.target.value, b])}
              style={fieldStyle}
            />
          </label>
          <label style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ ...labelTextStyle, fontSize: '0.8125rem' }}>to</span>
            <input
              type={inputType}
              value={inputType === 'date' ? toDateInput(b) : b}
              onChange={(e) => setValue(c.name, [a, e.target.value])}
              style={fieldStyle}
            />
          </label>
        </div>
      );
    }
    if (c.data_type === 'date') {
      return (
        <input
          type="date"
          value={toDateInput(cur)}
          onChange={(e) => setValue(c.name, e.target.value)}
          style={{ ...fieldStyle, flex: 1 }}
        />
      );
    }
    if (c.data_type === 'number') {
      return (
        <input
          type="number"
          value={cur == null ? '' : String(cur)}
          onChange={(e) => setValue(c.name, e.target.value)}
          style={{ ...fieldStyle, flex: 1 }}
        />
      );
    }
    return (
      <input
        type="text"
        value={cur == null ? '' : String(cur)}
        onChange={(e) => setValue(c.name, e.target.value)}
        style={{ ...fieldStyle, flex: 1 }}
      />
    );
  };

  // The lookup attached to a criterion (case-insensitive name match).
  const lookupFor = (name: string): ColumnLookup | undefined => {
    if (!lookups) return undefined;
    const lower = name.toLowerCase();
    const k = Object.keys(lookups).find((x) => x.toLowerCase() === lower);
    return k ? lookups[k] : undefined;
  };

  // Add/replace a criterion's lookup (define mode). Keys are stored lower-cased
  // so they match the backend's case-insensitive resolution.
  const saveLookup = (name: string, lk: ColumnLookup) => {
    const lower = name.toLowerCase();
    const next: Record<string, ColumnLookup> = {};
    for (const [k, v] of Object.entries(lookups ?? {})) {
      if (k.toLowerCase() !== lower) next[k] = v;
    }
    next[lower] = lk;
    onChangeLookups?.(next);
  };
  const clearLookup = (name: string) => {
    const lower = name.toLowerCase();
    const next: Record<string, ColumnLookup> = {};
    for (const [k, v] of Object.entries(lookups ?? {})) {
      if (k.toLowerCase() !== lower) next[k] = v;
    }
    onChangeLookups?.(next);
  };

  // Label (with mandatory asterisk) over the input, plus a 🔍 picker (show/use)
  // and a config button (define mode) sitting to the right of the input — the
  // same arrangement a transaction form lookup field uses.
  const renderField = (c: CriterionDef) => {
    if ((c as any).hidden) return null;
    const locked = !!(c as any).protect;
    const lk = lookupFor(c.name);
    const showPicker = !!lk && !!fetchLookup && !locked;
    const showConfig = !!onChangeLookups;
    const mandatory = isMandatory(c);
    return (
      <fieldset key={c.name} disabled={locked} title={locked ? 'Set by your administrator' : undefined}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, border: 0, padding: 0, margin: 0, opacity: locked ? 0.8 : 1 }}>
        <span style={labelTextStyle} title={c.column_hint ? `on ${c.column_hint}` : undefined}>
          {labelOf(c)}
          {mandatory && <span style={{ color: '#ef4444' }}> *</span>}
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          {renderInput(c)}
          {showPicker && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setPickerFor(c)}
              title={`Open lookup${lk ? `: ${lk.table}` : ''}`}
              style={lookupBtnStyle}
            >
              <Search size={14} />
            </button>
          )}
          {showConfig && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setConfigFor(c)}
              title={lk ? `Edit lookup: ${lk.table}(${lk.value_column})` : 'Add a reference-table lookup'}
              style={{ ...lookupBtnStyle, color: lk ? 'var(--color-accent)' : undefined }}
            >
              <SlidersHorizontal size={14} />
            </button>
          )}
        </div>
      </fieldset>
    );
  };

  const helperLine = useMemo(() => {
    if (items.length === 0) return null;
    const helpers = items.filter((i) => i.default_helper).map((i) => `${i.name}=@${i.default_helper}`);
    if (!helpers.length) return null;
    return `Auto-defaults: ${helpers.join(', ')}`;
  }, [items]);

  if (!sourceSql || !sourceSql.includes('?.')) {
    return (
      <div style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
        No criteria. Add <code>?.NAME</code> placeholders to the SQL above and they will
        appear here as typed prompts.
        <br />
        Examples: <code>= ?.region.string</code>, <code>&gt;= ?.start_date.date</code>,
        {' '}<code>in ?.product_codes.string</code>,
        {' '}<code>between ?.min_amt.number and ?.max_amt.number</code>,
        {' '}<code>?.start_date.date.@1stdayofmonth</code>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, justifyContent: 'flex-end', marginTop: -2 }}>
        {loading && (
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flex: 1 }}>
            Refreshing…
          </span>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          title="Re-parse SQL"
          onClick={() => {
            setLoading(true);
            RunApi.criteria({ source_sql: sourceSql })
              .then((r) => setItems(r.items))
              .catch((e) => setError((e as Error).message))
              .finally(() => setLoading(false));
          }}
          style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {error && (
        <div style={{
          fontSize: '0.8125rem', color: 'var(--color-warning, #f5a524)',
          marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {error}
        </div>
      )}
      {helperLine && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 8 }}>
          {helperLine}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${dense ? 210 : 260}px, 1fr))`,
          columnGap: 22,
          rowGap: 16,
          alignItems: 'start',
        }}
      >
        {items.map(renderField)}
      </div>

      {pickerFor && fetchLookup && (
        <LookupPickerDialog
          open
          column={pickerFor.label || pickerFor.name}
          table={lookupFor(pickerFor.name)?.table}
          currentValue={valueFor(pickerFor.name)}
          search={(text) => fetchLookup(pickerFor.name, text)}
          onClose={() => setPickerFor(null)}
          onSelect={(row) => setValue(pickerFor.name, row.value)}
        />
      )}

      {configFor && onChangeLookups && (
        <LookupConfigDialog
          key={configFor.name}
          name={configFor.label || configFor.name}
          subjectKind="criterion"
          value={lookupFor(configFor.name)}
          onClose={() => setConfigFor(null)}
          onSave={(lk) => { saveLookup(configFor.name, lk); setConfigFor(null); }}
          onClear={() => { clearLookup(configFor.name); setConfigFor(null); }}
        />
      )}
    </div>
  );
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 7px',
  borderRadius: 10,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)',
  color: 'var(--color-text-secondary)',
  fontSize: '0.8125rem',
};
